import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { getPlatformAdminErrorMessage, platformAdminApi } from '../api/platformAdminApi';
import type {
  PlatformAssignableBot,
  PlatformTenant,
  PlatformUser,
  PlatformUserBotAccessMode,
  TenantFeature,
} from '../types/platformAdmin.types';
import { MODULE_FEATURES } from '../types/platformAdmin.types';

type UserForm = {
  email: string;
  password: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'USER' | 'OPERATOR';
  tenantId: string;
  status: 'ACTIVE' | 'INACTIVE';
  telegramId: string;
  whatsappPhone: string;
};

type AssignableBotGroup = {
  instanceId: string;
  instanceName: string;
  bots: PlatformAssignableBot[];
};

function createEmptyForm(currentTenantId?: string | null): UserForm {
  return {
    email: '',
    password: '',
    role: 'USER',
    tenantId: currentTenantId || '',
    status: 'ACTIVE',
    telegramId: '',
    whatsappPhone: '',
  };
}

function getChannelIdentityError(form: UserForm): string | null {
  if (form.role === 'SUPERADMIN') return null;
  const telegramId = form.telegramId.trim();
  if (telegramId) {
    const digits = telegramId.replace(/\D/g, '').slice(0, 32);
    if (digits.length < 5) {
      return 'El ID de Telegram debe ser numérico (el que te da Telegram, no el @usuario).';
    }
  }
  if (form.role !== 'USER') return null;
  const phone = form.whatsappPhone.trim();
  if (phone) {
    const digits = phone.replace(/\D/g, '').slice(0, 15);
    if (digits.length < 8 || digits.length > 15) {
      return 'El celular de WhatsApp debe estar en formato internacional, por ejemplo +5491112345678.';
    }
  }
  return null;
}

function getUserFormError(
  form: UserForm,
  isNew: boolean,
  isSuperAdmin: boolean,
  currentTenantId?: string | null
): string | null {
  const email = form.email.trim();
  if (!email) return 'Ingresá el email del usuario que querés crear.';
  if (!email.includes('@') || !email.includes('.'))
    return 'Ingresá un email válido, por ejemplo usuario@empresa.com.';

  if (isNew && form.password.length < 8) {
    return 'La contraseña inicial debe tener al menos 8 caracteres. Ejemplo: Test1234!.';
  }

  if (!isNew && form.password && form.password.length < 8) {
    return 'La nueva contraseña debe tener al menos 8 caracteres.';
  }

  if (form.role !== 'SUPERADMIN' && !form.tenantId && !currentTenantId) {
    return isSuperAdmin
      ? 'Seleccioná un tenant para el usuario. Los usuarios ADMIN y USER siempre deben pertenecer a un tenant.'
      : 'No se pudo detectar tu tenant actual. Volvé a iniciar sesión antes de crear usuarios.';
  }

  return getChannelIdentityError(form);
}

type FeatureAccessMode = 'ALL_TENANT_MODULES' | 'SELECTED_MODULES';

function tenantModuleFeatures(features: TenantFeature[]): TenantFeature[] {
  const allowed = new Set(MODULE_FEATURES.map(item => item.value));
  return features.filter(feature => allowed.has(feature));
}

function canManageUserModules(
  actorIsSuperAdmin: boolean,
  targetRole: UserForm['role'],
): boolean {
  if (targetRole === 'SUPERADMIN') {
    return false;
  }
  if (actorIsSuperAdmin) {
    return true;
  }
  return targetRole === 'USER';
}

function groupAssignableBots(bots: PlatformAssignableBot[]): AssignableBotGroup[] {
  const groups = new Map<string, AssignableBotGroup>();
  for (const bot of bots) {
    const group = groups.get(bot.instanceId) ?? {
      instanceId: bot.instanceId,
      instanceName: bot.instanceName,
      bots: [],
    };
    group.bots.push(bot);
    groups.set(bot.instanceId, group);
  }
  return Array.from(groups.values());
}

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const isNew = id === 'new';
  const userId = id && !isNew ? Number(id) : null;
  const [form, setForm] = useState<UserForm>(createEmptyForm(currentUser?.tenantId));
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [assignableBots, setAssignableBots] = useState<PlatformAssignableBot[]>([]);
  const [botAccessMode, setBotAccessMode] =
    useState<PlatformUserBotAccessMode>('ALL_TENANT_BOTS');
  const [selectedInstanceBotIds, setSelectedInstanceBotIds] = useState<string[]>([]);
  const [loadingBotAccess, setLoadingBotAccess] = useState(false);
  const [featureAccessMode, setFeatureAccessMode] =
    useState<FeatureAccessMode>('ALL_TENANT_MODULES');
  const [tenantModuleList, setTenantModuleList] = useState<TenantFeature[]>([]);
  const [selectedModuleFeatures, setSelectedModuleFeatures] = useState<TenantFeature[]>([]);
  const [loadingFeatureAccess, setLoadingFeatureAccess] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const loadTenants = async () => {
      try {
        setTenants(await platformAdminApi.listTenants());
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Error al cargar tenants');
      }
    };
    loadTenants();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!userId) return;
    const loadUser = async () => {
      try {
        setLoading(true);
        const user: PlatformUser = await platformAdminApi.getUser(userId);
        setForm({
          email: user.email,
          password: '',
          role: user.role,
          tenantId: user.tenantId || '',
          status: user.status,
          telegramId: user.telegramId || '',
          whatsappPhone: user.whatsappPhone ? `+${user.whatsappPhone}` : '',
        });
        const botAccess = await platformAdminApi.getUserBotAccess(userId);
        setBotAccessMode(botAccess.mode);
        setSelectedInstanceBotIds(botAccess.assignedInstanceBotIds);
        setAssignableBots(botAccess.assignableBots);
        if (canManageUserModules(isSuperAdmin, user.role)) {
          const featureAccess = await platformAdminApi.getUserFeatureAccess(userId);
          setTenantModuleList(featureAccess.tenantFeatures);
          const hasCustomModules = featureAccess.featuresEnabled.length > 0;
          setFeatureAccessMode(hasCustomModules ? 'SELECTED_MODULES' : 'ALL_TENANT_MODULES');
          setSelectedModuleFeatures(
            hasCustomModules ? featureAccess.featuresEnabled : featureAccess.tenantFeatures,
          );
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Error al cargar usuario');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [userId, isSuperAdmin]);

  useEffect(() => {
    if (!canManageUserModules(isSuperAdmin, form.role)) {
      return;
    }
    const tenantId = form.tenantId || currentUser?.tenantId;
    if (!tenantId) {
      setTenantModuleList([]);
      return;
    }
    const syncTenantModules = async () => {
      try {
        setLoadingFeatureAccess(true);
        if (isSuperAdmin) {
          const tenantList =
            tenants.length > 0 ? tenants : await platformAdminApi.listTenants();
          const tenant = tenantList.find(item => item.id === tenantId);
          const modules = tenantModuleFeatures(tenant?.config.features ?? []);
          setTenantModuleList(modules);
          if (isNew) {
            setFeatureAccessMode('ALL_TENANT_MODULES');
            setSelectedModuleFeatures(modules);
          }
          return;
        }
        if (isNew) {
          const modules = tenantModuleFeatures((currentUser?.tenantFeatures ?? []) as TenantFeature[]);
          setTenantModuleList(modules);
          setFeatureAccessMode('ALL_TENANT_MODULES');
          setSelectedModuleFeatures(modules);
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Error al cargar módulos del tenant');
      } finally {
        setLoadingFeatureAccess(false);
      }
    };
    void syncTenantModules();
  }, [
    currentUser?.tenantFeatures,
    currentUser?.tenantId,
    form.role,
    form.tenantId,
    isNew,
    isSuperAdmin,
    tenants,
  ]);

  useEffect(() => {
    if (!isNew || form.role === 'SUPERADMIN') return;
    const tenantId = form.tenantId || currentUser?.tenantId;
    if (!tenantId) {
      setAssignableBots([]);
      return;
    }
    loadAssignableBots(tenantId);
  }, [currentUser?.tenantId, form.role, form.tenantId, isNew]);

  const loadAssignableBots = async (tenantId: string) => {
    try {
      setLoadingBotAccess(true);
      const instances = await chatbotAdminApi.listInstances(isSuperAdmin ? tenantId : undefined);
      const botGroups = await Promise.all(
        instances.map(async instance => {
          const bots = await chatbotAdminApi.listBots(instance.id);
          return bots.map(bot => ({
            instanceBotId: bot.id || `${instance.id}:${bot.botId}`,
            instanceId: instance.id,
            instanceName: instance.name,
            botId: bot.botId,
            botName: bot.name,
            isActive: bot.isActive,
            displayOrder: bot.displayOrder,
            createdAt: String(bot.createdAt),
          }));
        })
      );
      setAssignableBots(botGroups.flat());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al cargar bots disponibles');
    } finally {
      setLoadingBotAccess(false);
    }
  };

  const toggleSelectedBot = (instanceBotId: string) => {
    setSelectedInstanceBotIds(current =>
      current.includes(instanceBotId)
        ? current.filter(id => id !== instanceBotId)
        : [...current, instanceBotId]
    );
  };

  const saveFeatureAccess = async (savedUserId: number, role: UserForm['role']) => {
    if (!canManageUserModules(isSuperAdmin, role)) {
      return;
    }
    const enabled =
      featureAccessMode === 'ALL_TENANT_MODULES'
        ? []
        : selectedModuleFeatures.filter(feature => tenantModuleList.includes(feature));
    await platformAdminApi.updateUserFeatureAccess(savedUserId, { featuresEnabled: enabled });
  };

  const toggleModuleFeature = (feature: TenantFeature) => {
    setSelectedModuleFeatures(current =>
      current.includes(feature) ? current.filter(item => item !== feature) : [...current, feature],
    );
  };

  const saveBotAccess = async (savedUserId: number, role: UserForm['role']) => {
    if (role === 'SUPERADMIN') return;
    await platformAdminApi.updateUserBotAccess(savedUserId, {
      mode: botAccessMode,
      instanceBotIds: botAccessMode === 'SELECTED_BOTS' ? selectedInstanceBotIds : [],
    });
  };

  const handleSave = async () => {
    const validationError = getUserFormError(form, isNew, isSuperAdmin, currentUser?.tenantId);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      const tenantId =
        form.role === 'SUPERADMIN' ? null : form.tenantId || currentUser?.tenantId || null;
      if (isNew) {
        const createdUser = await platformAdminApi.createUser({
          email: form.email,
          password: form.password,
          role: form.role,
          tenantId,
          status: form.status,
          telegramId: form.role === 'SUPERADMIN' ? null : form.telegramId.trim() || null,
          whatsappPhone: form.role === 'USER' ? form.whatsappPhone.trim() || null : null,
        });
        await saveBotAccess(createdUser.id, form.role);
        await saveFeatureAccess(createdUser.id, form.role);
        toast.success('Usuario creado');
      } else if (userId) {
        await platformAdminApi.updateUser(userId, {
          email: form.email,
          role: form.role,
          tenantId,
          status: form.status,
          telegramId: form.role === 'SUPERADMIN' ? null : form.telegramId.trim() || null,
          whatsappPhone: form.role === 'USER' ? form.whatsappPhone.trim() || null : null,
        });
        if (form.password) {
          await platformAdminApi.resetUserPassword(userId, form.password);
        }
        await saveBotAccess(userId, form.role);
        await saveFeatureAccess(userId, form.role);
        toast.success('Usuario actualizado');
      }
      navigate('/admin/users');
    } catch (error) {
      toast.error(getPlatformAdminErrorMessage(error, 'No se pudo guardar el usuario'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className='container mx-auto py-6'>Cargando usuario...</div>;
  }

  return (
    <div className='container mx-auto py-6 max-w-3xl space-y-4'>
      <Button variant='ghost' onClick={() => navigate('/admin/users')}>
        <ArrowLeft className='h-4 w-4 mr-2' />
        Volver
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'Nuevo Usuario' : 'Editar Usuario'}</CardTitle>
          <CardDescription>
            Asigná rol, tenant y, si corresponde, Telegram o WhatsApp. En Usuario van a la whitelist
            del chat. En Admin, el ID de Telegram sirve para avisos de Excepciones.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={event => setForm({ ...form, email: event.target.value })}
              />
            </div>
            <div>
              <Label>{isNew ? 'Contraseña inicial' : 'Nueva contraseña (opcional)'}</Label>
              <Input
                type='password'
                value={form.password}
                onChange={event => setForm({ ...form, password: event.target.value })}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <select
                value={form.role}
                onChange={event =>
                  setForm({ ...form, role: event.target.value as UserForm['role'] })
                }
                disabled={!isSuperAdmin}
                className='w-full border border-input bg-background rounded-md p-2'
              >
                {isSuperAdmin && <option value='SUPERADMIN'>Superadmin</option>}
                {isSuperAdmin && <option value='ADMIN'>Admin</option>}
                <option value='OPERATOR'>Operador</option>
                <option value='USER'>Usuario</option>
              </select>
            </div>
            <div>
              <Label>Estado</Label>
              <select
                value={form.status}
                onChange={event =>
                  setForm({ ...form, status: event.target.value as UserForm['status'] })
                }
                className='w-full border border-input bg-background rounded-md p-2'
              >
                <option value='ACTIVE'>Activo</option>
                <option value='INACTIVE'>Inactivo</option>
              </select>
            </div>
            {form.role !== 'SUPERADMIN' && (
              <div className='md:col-span-2'>
                <Label>Tenant</Label>
                {isSuperAdmin ? (
                  <select
                    value={form.tenantId}
                    onChange={event => {
                      setForm({ ...form, tenantId: event.target.value });
                      setBotAccessMode('ALL_TENANT_BOTS');
                      setSelectedInstanceBotIds([]);
                      setFeatureAccessMode('ALL_TENANT_MODULES');
                      setSelectedModuleFeatures([]);
                    }}
                    className='w-full border border-input bg-background rounded-md p-2'
                  >
                    <option value=''>Seleccionar tenant</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input value={currentUser?.tenantName || currentUser?.tenantId || ''} disabled />
                )}
              </div>
            )}
          </div>

          {form.role === 'ADMIN' && (
            <div className='space-y-4 rounded-lg border p-4'>
              <div>
                <Label>Aviso por Telegram</Label>
                <p className='text-sm text-muted-foreground'>
                  Si un cliente pide un operador, mandamos un aviso a este ID. No entra a la
                  whitelist del bot. El admin tiene que haber iniciado una vez el bot de Telegram
                  del tenant.
                </p>
              </div>
              <div>
                <Label htmlFor='admin-telegram-id'>ID de Telegram</Label>
                <Input
                  id='admin-telegram-id'
                  inputMode='numeric'
                  placeholder='123456789'
                  value={form.telegramId}
                  onChange={event => setForm({ ...form, telegramId: event.target.value.slice(0, 32) })}
                />
              </div>
            </div>
          )}

          {form.role === 'USER' && (
            <div className='space-y-4 rounded-lg border p-4'>
              <div>
                <Label>Identidad en canales</Label>
                <p className='text-sm text-muted-foreground'>
                  Se carga una sola vez. El email, el ID de Telegram y el celular habilitan la
                  whitelist de web, Telegram y WhatsApp de los bots asignados.
                </p>
              </div>
              <div className='grid gap-4 md:grid-cols-2'>
                <div>
                  <Label htmlFor='user-telegram-id'>ID de Telegram</Label>
                  <Input
                    id='user-telegram-id'
                    inputMode='numeric'
                    placeholder='123456789'
                    value={form.telegramId}
                    onChange={event => setForm({ ...form, telegramId: event.target.value.slice(0, 32) })}
                  />
                </div>
                <div>
                  <Label htmlFor='user-whatsapp-phone'>Celular WhatsApp</Label>
                  <Input
                    id='user-whatsapp-phone'
                    placeholder='+5491112345678'
                    value={form.whatsappPhone}
                    onChange={event => setForm({ ...form, whatsappPhone: event.target.value.slice(0, 20) })}
                  />
                </div>
              </div>
            </div>
          )}

          {form.role !== 'SUPERADMIN' && (
            <div className='space-y-4 rounded-lg border p-4'>
              <div>
                <Label>Acceso a bots</Label>
                <p className='text-sm text-muted-foreground'>
                  Definí si el usuario puede usar todos los bots del tenant o solo bots específicos.
                </p>
              </div>
              <div className='grid gap-2 md:grid-cols-2'>
                <label className='flex items-start gap-2 rounded-md border p-3'>
                  <input
                    type='radio'
                    name='botAccessMode'
                    checked={botAccessMode === 'ALL_TENANT_BOTS'}
                    onChange={() => setBotAccessMode('ALL_TENANT_BOTS')}
                  />
                  <span>
                    <span className='block font-medium'>Todos los bots del tenant</span>
                    <span className='text-xs text-muted-foreground'>
                      Mantiene el comportamiento actual para este usuario.
                    </span>
                  </span>
                </label>
                <label className='flex items-start gap-2 rounded-md border p-3'>
                  <input
                    type='radio'
                    name='botAccessMode'
                    checked={botAccessMode === 'SELECTED_BOTS'}
                    onChange={() => setBotAccessMode('SELECTED_BOTS')}
                  />
                  <span>
                    <span className='block font-medium'>Solo bots seleccionados</span>
                    <span className='text-xs text-muted-foreground'>
                      Restringe acceso en portal, visor, métricas y exportaciones.
                    </span>
                  </span>
                </label>
              </div>

              {botAccessMode === 'SELECTED_BOTS' && (
                <div className='space-y-3'>
                  {loadingBotAccess && (
                    <p className='text-sm text-muted-foreground'>Cargando bots disponibles...</p>
                  )}
                  {!loadingBotAccess && assignableBots.length === 0 && (
                    <p className='text-sm text-muted-foreground'>
                      No hay bots disponibles para el tenant seleccionado.
                    </p>
                  )}
                  {groupAssignableBots(assignableBots).map(group => (
                    <div key={group.instanceId} className='rounded-md border p-3'>
                      <div className='mb-2 font-medium'>{group.instanceName}</div>
                      <div className='grid gap-2 md:grid-cols-2'>
                        {group.bots.map(bot => (
                          <label key={bot.instanceBotId} className='flex items-center gap-2 text-sm'>
                            <input
                              type='checkbox'
                              checked={selectedInstanceBotIds.includes(bot.instanceBotId)}
                              onChange={() => toggleSelectedBot(bot.instanceBotId)}
                            />
                            <span>{bot.botName}</span>
                            {!bot.isActive && (
                              <span className='text-xs text-muted-foreground'>(inactivo)</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {canManageUserModules(isSuperAdmin, form.role) && (
            <div className='space-y-4 rounded-lg border p-4'>
              <div>
                <Label>Acceso a módulos</Label>
                <p className='text-sm text-muted-foreground'>
                  Solo podés habilitar módulos que el superadmin ya activó para el tenant. Si el
                  tenant no tiene un módulo, no aparecerá aquí.
                </p>
              </div>
              <div className='grid gap-2 md:grid-cols-2'>
                <label className='flex items-start gap-2 rounded-md border p-3'>
                  <input
                    type='radio'
                    name='featureAccessMode'
                    checked={featureAccessMode === 'ALL_TENANT_MODULES'}
                    onChange={() => {
                      setFeatureAccessMode('ALL_TENANT_MODULES');
                      setSelectedModuleFeatures(tenantModuleList);
                    }}
                  />
                  <span>
                    <span className='block font-medium'>Todos los módulos del tenant</span>
                    <span className='text-xs text-muted-foreground'>
                      Hereda Chatbot, WhatsApp, Telegram y el resto según el tenant.
                    </span>
                  </span>
                </label>
                <label className='flex items-start gap-2 rounded-md border p-3'>
                  <input
                    type='radio'
                    name='featureAccessMode'
                    checked={featureAccessMode === 'SELECTED_MODULES'}
                    onChange={() => setFeatureAccessMode('SELECTED_MODULES')}
                  />
                  <span>
                    <span className='block font-medium'>Solo módulos seleccionados</span>
                    <span className='text-xs text-muted-foreground'>
                      Restringe el menú de administración para este usuario.
                    </span>
                  </span>
                </label>
              </div>

              {featureAccessMode === 'SELECTED_MODULES' && (
                <div className='space-y-3'>
                  {loadingFeatureAccess && (
                    <p className='text-sm text-muted-foreground'>Cargando módulos disponibles...</p>
                  )}
                  {!loadingFeatureAccess && tenantModuleList.length === 0 && (
                    <p className='text-sm text-muted-foreground'>
                      El tenant no tiene módulos habilitados. Configuralos en Tenants.
                    </p>
                  )}
                  <div className='grid gap-2 md:grid-cols-2'>
                    {MODULE_FEATURES.filter(item => tenantModuleList.includes(item.value)).map(
                      module => (
                        <label
                          key={module.value}
                          className='flex items-center gap-2 rounded-md border p-3 text-sm'
                        >
                          <input
                            type='checkbox'
                            checked={selectedModuleFeatures.includes(module.value)}
                            onChange={() => toggleModuleFeature(module.value)}
                          />
                          <span>{module.label}</span>
                        </label>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className='flex justify-end'>
            <Button onClick={handleSave} disabled={saving}>
              <Save className='h-4 w-4 mr-2' />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
